<template>
	<SimpleModal @close="onClose" :loadingOverlay="isInitializing">
		<div class="pronto-user-modal">
			<div class="profile-pic-container">
				<div v-if="isBlocked" class="blocked-text">
					{{ $t('people_block_blocked') }}
				</div>
				<ProfilePic
					:user="user"
					size="86px"
					class="profile-pic"
					:class="{ 'blocked-opacity': isBlocked }"
				/>
			</div>
			<div class="user-name">
				<div class="user-name-row">
					<div v-if="!isMyUser && user.isonline" class="online-indicator"></div>
					<UserName :user="user" class="user-name-text" />
				</div>
				<div class="last-active" v-text="lastActiveText" />
			</div>
			<div class="user-details" v-if="showDetails">
				<Tag
					v-if="showPronouns && pronouns"
					class="user-pronouns"
					large
					solid
					rounded
					:uppercase="false"
					>{{ pronouns }}</Tag
				>
				<div class="user-title" v-if="title" v-text="title" />
			</div>
			<div
				class="user-actions"
				:class="{ stretch: actionCount === 1, stack: actionCount > 2 }"
			>
				<Button
					v-for="{ key, label, ...rest } in actions"
					:key="key"
					:loading="loadingAction === key"
					v-bind="rest"
				>
					{{ $t(label) }}
				</Button>
			</div>
			<Button
				block
				class="mutual-groups"
				@click="showMutualGroups = true"
				v-if="!isMyUser"
			>
				{{
					mutualGroups.length > 0
						? $t(
								'chat_bubbles_common_count.variable',
								[mutualGroups.length],
								mutualGroups.length
						  )
						: $t('groupsettings_bubbles_common_count_none')
				}}
			</Button>
			<div
				class="chat-section"
				v-if="currentChat && !currentChat.isdm && !currentChat.is_thread"
			>
				<div class="chat-section-title" v-text="currentChat.displayname" />
				<Dropdown
					:options="roleOptions"
					v-model="currentRole"
					global
					:loading="loadingAction === 'role'"
					:disabled="user.inactive"
					v-if="membership && canChangeRole"
					@change="changeRole"
				/>
				<Tag
					class="chat-section-role-static"
					large
					v-if="membership && !canChangeRole"
				>
					{{ $t(`model_role_${currentRole}`) }}
				</Tag>
				<Button
					block
					link
					@click="removeFromChat"
					:icon="BanIcon"
					:loading="loadingAction === 'remove'"
					class="remove-from-chat"
					v-if="membership && canRemoveMember && !isMyUser"
				>
					{{ $t('people_removefromgroup_button_title') }}
				</Button>
				<Button
					block
					link
					@click="leaveGroup"
					:icon="BanIcon"
					:loading="loadingAction === 'leave'"
					class="remove-from-chat"
					v-if="membership && canRemoveMember && isMyUser"
				>
					{{ $t('people_leavegroup_button_title') }}
				</Button>
				<Tag v-if="!membership" class="removed-from-chat" large>
					{{ $t('people_user_no_longer_in_group') }}
				</Tag>
			</div>
		</div>
	</SimpleModal>

	<!-- MUTUAL GROUPS MODAL -->
	<StandardModal
		v-if="showMutualGroups"
		open
		small
		class="mutual-groups-modal"
		:title="
			$t(
				'chat_bubbles_common_count.variable',
				[mutualGroups.length],
				mutualGroups.length
			)
		"
		hideConfirm
		:cancelText="$t('common_button_done')"
		@cancel="showMutualGroups = false"
	>
		<Table
			:columns="[{ field: 'displayname' }]"
			:rows="mutualGroups"
			hideHeader
			scrollable
			height="232px"
		>
			<template #cell-displayname="{ data }">
				<div class="mutual-group">
					<div
						v-if="data.category"
						class="category"
						v-text="data.category.displayname"
					/>
					<div class="group-name" v-text="data.displayname" />
				</div>
			</template>
		</Table>
	</StandardModal>
</template>

<script setup>
import { ref, computed, onMounted, markRaw, nextTick } from 'vue'
import SimpleModal from './SimpleModal'
import StandardModal from './StandardModal'
import Table from './Table'
import Button from './Button'
import ProfilePic from './ProfilePic'
import Tag from './Tag'
import UserName from './UserName'
import Dropdown from './Dropdown'
import { SearchIcon, UserSlashIcon, CommentsIcon, BanIcon } from '@/icons'
import bus, { useEventBus } from '@/helpers/bus'
import { calculateLastActiveText } from '@/helpers/utils'
import store, {
	getMyUser,
	getUser,
	getMyUserId,
	getChat,
	getCurrentChat,
	getDMChats,
	getMembershipForUser,
	normalizeChat,
	sortChatsByDisplayName,
	setUser,
	setMembership,
	deleteMembership,
	getMyMembershipForBubble,
} from '@/store'
import { orgFlag, USER_PRONOUNS, USER_TITLE, SEARCH } from '@/helpers/flags'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
	redirectToRecentsOrChat,
	redirectToLastActiveGroup,
	handleRPCError,
} from '@/helperMethods'
import { fetchMutualGroups, fetchUserInfo } from '@/api/user'
import { createDM } from '@/api/chat'
import { post } from '@/helpers/api'
import {
	chatCan,
	UPDATE_MEMBERSHIP_ROLE,
	REMOVE_MEMBER,
} from '@/helpers/permissions'
import { useAlert } from './Alert'
import { showAlertModal, hideAlertModal } from './AlertModal'
import { normalizeCategory } from '@/store/chats'
import { checkAndFetchMembership } from '@/api/membership'

const { t } = useI18n()
const router = useRouter()
const emit = defineEmits(['close'])
const props = defineProps({
	userId: {
		type: [Number, String],
		required: true,
	},
	chatId: {
		type: [Number, String],
		default: null,
	},
})
const user = ref(getUser(props.userId))
const showPronouns = orgFlag(USER_PRONOUNS)
const canSearch = orgFlag(SEARCH) && !store.config.wrapper
const currentChat = props.chatId ? getChat(props.chatId) : getCurrentChat()
const loadingAction = ref(null)
const isInitializing = ref(true)
const mutualGroups = ref(0)
const showMutualGroups = ref(false)
const membership = ref(getMembershipForUser(props.userId))
const currentRole = ref(membership.value?.role)
const alert = useAlert()

useEventBus('user-update', () => {
	user.value = getUser(props.userId)
	membership.value = getMembershipForUser(props.userId)
	if (membership.value) {
		currentRole.value = membership.value.role
	}
})

onMounted(async () => {
	const promises = [fetchUserInfo(props.userId), getMutualGroups()]
	if (currentChat.id) {
		promises.push(checkAndFetchMembership(props.userId, currentChat.id))
	}
	await Promise.all(promises)
	await nextTick()
	isInitializing.value = false
})

const actionPropsByKey = {
	message: {
		block: true,
		icon: markRaw(CommentsIcon),
		iconProps: { type: 'regular' },
		label: 'people_directmessage_button_title',
		onClick: () => startDM(),
	},
	edit: {
		block: true,
		pronto: true,
		label: 'settings_action_edit',
		onClick: () => goToUserSettings(),
	},
	search: {
		icon: markRaw(SearchIcon),
		iconProps: { type: 'regular' },
		label: 'admin_people_search_action',
		onClick: () => goToUserSearch(),
	},
	block: {
		icon: markRaw(UserSlashIcon),
		iconProps: { type: 'regular' },
		label: 'people_block_button_title',
		onClick: () => showBlockConfirmation(),
	},
	unblock: {
		danger: true,
		block: true,
		label: 'people_unblock_button_title',
		onClick: () => unBlockUser(),
	},
}

const lastActiveText = computed(() => calculateLastActiveText(user.value))
const pronouns = computed(() => {
	if (!orgFlag(USER_PRONOUNS)) return null
	return user.value?.pronouns
})
const title = computed(() => {
	if (!orgFlag(USER_TITLE)) return null
	return user.value?.title
})
const showDetails = computed(() => !!(pronouns.value || title.value))
const dmChat = computed(
	() => getDMChats().find((b) => b.dmpartner_id === user.value?.id) || null
)
const isMyUser = computed(() => getMyUserId() === user.value?.id)
const isBlocked = computed(
	() => getMyUser().blocked?.includes(user.value?.id) || false
)
const canRemoveMember = computed(() => {
	if (!chatCan(currentChat, REMOVE_MEMBER)) return false
	const myMemb = getMyMembershipForBubble(currentChat.id)
	const userMemb = getMembershipForUser(props.userId)
	if (myMemb?.role === 'owner') return true
	if (myMemb?.role === 'member' && userMemb?.role === 'owner') return false
	return false
})
const canChangeRole = computed(
	() =>
		!isMyUser.value &&
		!user.value?.isAdmin &&
		chatCan(currentChat, UPDATE_MEMBERSHIP_ROLE)
)
const roleOptions = computed(() =>
	['member', 'owner'].map((id) => ({
		id,
		label: t(`model_role_${id}`),
	}))
)
const actions = computed(() => {
	if (dmChat.value && user.value?.inactive) {
		// for inactive users, only show a DM button if DM already exists
		return ['message']
	}
	const actionKeys = []
	if (isMyUser.value) {
		actionKeys.push('edit')
		if (canSearch) {
			actionKeys.push('search')
		}
	}
	if (!isBlocked.value && !isMyUser.value && !user.value?.inactive) {
		actionKeys.push('message')
		if (canSearch) {
			actionKeys.push('search')
		}
		actionKeys.push('block')
	}
	if (isBlocked.value) {
		actionKeys.push('unblock')
	}
	return actionKeys.map((key) => ({ key, ...actionPropsByKey[key] }))
})
const actionCount = computed(() => actions.value?.length || 0)

function onClose() {
	emit('close')
}

function removeFromChat() {
	showAlertModal({
		title: t('people_removefromgroup_button_title'),
		description: t('people_removefromgroup_confirm_warning_desc', {
			UserName: user.value.fullname,
		}),
		confirmText: t('common_menu_remove'),
		confirmProps: { danger: true },
		hideCancel: false,
		icon: markRaw(BanIcon),
		onConfirm: async () => {
			hideAlertModal()
			await kickFromChat()
		},
	})
}

async function kickFromChat() {
	try {
		loadingAction.value = 'remove'
		const { data } = await post('/api/v1/bubble.kick', {
			bubble_id: currentChat.id,
			users: [user.value.id],
		})
		handleRPCError(data)
		// remove local membership
		const memb = getMembershipForUser(props.userId)
		deleteMembership(memb.id)
		bus.$emit('removedUserFromProfileCard', {
			userId: props.userId,
			chatId: currentChat.id,
		})
		alert.success()
		onClose()
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}

function leaveGroup() {
	showAlertModal({
		title: t('people_leavegroup_button_title'),
		description: t('groupsettings_leave_confirm_explanation', {
			GroupName: currentChat.displayname,
		}),
		confirmText: t('chat_drawer_action_leave'),
		confirmProps: { danger: true },
		hideCancel: false,
		icon: markRaw(BanIcon),
		onConfirm: async () => {
			hideAlertModal()
			await leaveChat()
		},
	})
}

async function leaveChat() {
	try {
		loadingAction.value = 'leave'
		const { data } = await post('/api/v1/bubble.leave', {
			bubble_id: currentChat.id,
		})
		handleRPCError(data)
		// remove local membership
		const memb = getMembershipForUser(props.userId)
		deleteMembership(memb.id)
		onClose()
		redirectToLastActiveGroup()
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}

async function getMutualGroups() {
	try {
		if (isMyUser.value) return
		const data = await fetchMutualGroups(user.value.id)
		handleRPCError(data)
		mutualGroups.value = data.bubbles
			.map((bubble) => ({
				...normalizeChat(bubble),
				category: normalizeCategory(bubble.category),
			}))
			.sort(sortChatsByDisplayName)
	} catch (error) {
		console.error(error)
		alert.error(error)
	}
}

function goToUserSettings() {
	onClose()
	router.push('/settings/' + user.value.id + '?s=profile')
}

function goToUserSearch() {
	router.push({
		name: 'search',
		query: { users: user.value.id, focus: 1 },
	})
	onClose()
}

function showBlockConfirmation() {
	showAlertModal({
		title: t('people_block_confirm_title', {
			UserName: user.value.fullname,
		}),
		description: t('people_block_confirm_message'),
		confirmText: t('people_block_button_title'),
		confirmProps: { danger: true },
		hideCancel: false,
		icon: markRaw(UserSlashIcon),
		onConfirm: async () => {
			hideAlertModal()
			await blockUser()
		},
	})
}

async function blockUser() {
	try {
		loadingAction.value = 'block'
		const { data } = await post('/api/v1/user.block', {
			user_id: user.value.id,
		})
		handleRPCError(data)
		setUser(data.user)
		store.auth.user.blocked = data.user.blocked
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}

async function unBlockUser() {
	try {
		loadingAction.value = 'unblock'
		const { data } = await post('/api/v1/user.unblock', {
			user_id: user.value.id,
		})
		handleRPCError(data)
		setUser(data.user)
		store.auth.user.blocked = data.user.blocked
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}

async function startDM() {
	// If DM already exists, just navigate to it
	if (dmChat.value) {
		onClose()
		redirectToRecentsOrChat(dmChat.value.id)
		return
	}

	// Otherwise, create a new DM
	try {
		loadingAction.value = 'message'
		const chat = await createDM([user.value.id])
		onClose()
		router.push('/chat/' + chat.id)
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}

async function changeRole(role) {
	const membership = getMembershipForUser(props.userId)
	if (membership?.role === role) return
	loadingAction.value = 'role'
	try {
		const { data } = await post('/api/v1/membership.setuserrole', {
			bubble_id: currentChat.id,
			user_id: user.value.id,
			role,
		})
		handleRPCError(data)
		setMembership(data.membership)
		bus.$emit('userRoleUpdated', {
			userId: props.userId,
			chatId: currentChat.id,
			role,
		})
		alert.success()
	} catch (error) {
		console.error(error)
		alert.error(error)
	} finally {
		loadingAction.value = null
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-user-modal {
	@include flex-column-center;
	gap: 16px;
	padding: 16px 20px;
	width: 330px;

	.profile-pic-container {
		position: relative;
		@include flex-center;

		.blocked-text {
			position: absolute;
			color: var(--red-200);
			@include fontFamily(900);
			font-size: 20px;
		}
		.blocked-opacity {
			opacity: 0.15;
		}
	}

	.user-name {
		@include flex-column-center;
		.user-name-row {
			display: flex;
			align-items: center;
			gap: 8px;
		}
		.online-indicator {
			width: 12px;
			height: 12px;
			border-radius: 50%;
			background: var(--teal-300);
			flex-shrink: 0;
		}
		.user-name-text {
			font-size: 22px;
			@include fontFamily(900);
		}
		.last-active {
			font-size: 14px;
			@include fontFamily(800);
			color: var(--neutral-500);
		}
	}

	.user-details {
		@include flex-column-center(4px);
		.user-title {
			font-size: 14px;
			@include fontFamily(800);
			color: var(--neutral-500);
		}
	}

	.user-actions {
		width: 100%;
		@include flex-center(12px);
		&.stack {
			.pronto-btn {
				height: 64px;
				flex: 1;
				padding: 0 8px;
				flex-direction: column;
				:deep(.pronto-icon) {
					font-size: 18px;
				}
			}
		}
		&.stretch {
			.pronto-btn {
				width: 100% !important;
			}
		}
		.pronto-btn {
			width: 113px;
			@include flex-center(8px);
			margin: 0 !important;
			&.default {
				border-color: var(--neutral-200);
			}
		}
	}

	.chat-section {
		@include flex-column-center(4px);
		padding-top: 16px;
		width: 100%;
		border-top: 1px solid var(--neutral-200);
		.chat-section-title {
			font-size: 18px;
		}
		.chat-section-role-text {
			font-size: 18px;
			@include fontFamily(800);
			color: var(--neutral-500);
		}
		.dropdown {
			margin-top: 8px;
			width: 100%;
			:deep(.dropdown-selected) {
				width: 100%;
			}
		}
		.remove-from-chat:not(.loading) {
			color: var(--neutral-500) !important;
			&:hover {
				color: var(--neutral-800) !important;
			}
		}
	}
}

.mutual-groups-modal {
	.mutual-group {
		font-family: Avenir;
		font-style: normal;
		color: var(--neutral-800);

		.category {
			font-weight: normal;
			font-size: 12px;
			line-height: 16px;
			text-transform: uppercase;
			@include ellipsis;
		}

		.group-name {
			@include fontFamily(800);
			font-size: 15px;
			line-height: 20px;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-user-modal {
		.pronto-btn {
			&.default {
				border-color: var(--neutral-100) !important;
				background: var(--neutral-100) !important;
				&:not(.disabled):hover {
					background: var(--neutral-0) !important;
					border-color: var(--neutral-0) !important;
				}
			}
		}
		.chat-section {
			border-color: var(--neutral-40040) !important;
		}
	}
}
</style>
